import { Doctor, Visit, Product, AIGeneratedInsights } from '../types';
import { mockData } from '../data/mockData';

const OPENAI_API_KEY = process.env.REACT_APP_OPENAI_API_KEY;
const OPENAI_MODEL = process.env.REACT_APP_OPENAI_MODEL || 'gpt-4o-mini';

// Simulate API delay
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const doctorService = {
  async getDoctorById(doctorId: string): Promise<Doctor | null> {
    await delay(300);
    return mockData.doctors.find(d => d.id === doctorId) || null;
  },

  async getVisitsByDoctorId(doctorId: string): Promise<Visit[]> {
    await delay(300);
    return mockData.visits[doctorId] || [];
  },

  async getLast10Visits(doctorId: string): Promise<Visit[]> {
    await delay(300);
    const visits = mockData.visits[doctorId] || [];
    return visits.slice(0, 10).sort((a, b) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  },

  async getProductById(productId: string): Promise<Product | null> {
    await delay(200);
    const product = mockData.products.find(
      p => p.id.toLowerCase() === productId.toLowerCase() || 
           p.name.toLowerCase() === productId.toLowerCase()
    );
    return product || null;
  },

  async getProductByName(productName: string): Promise<Product | null> {
    await delay(200);
    const product = mockData.products.find(
      p => p.name.toLowerCase() === productName.toLowerCase()
    );
    return product || product || null;
  },

  async getAllProducts(): Promise<Product[]> {
    await delay(200);
    return mockData.products;
  },

  async generateAIPersona(doctorId: string): Promise<AIGeneratedInsights> {
    await delay(200);
    const doctor = await this.getDoctorById(doctorId);
    const visits = await this.getVisitsByDoctorId(doctorId);
    const products = mockData.products;

    if (!doctor) {
      throw new Error('Doctor not found');
    }

    // If no API key, throw error
    if (!OPENAI_API_KEY) {
      throw new Error('No OpenAI API key set. Please set OPENAI_API_KEY in your .env file.');
    }

    // Compose prompt
    const systemPrompt = `You are an expert assistant that generates concise AI-driven CRM insights for field medical reps. Produce a JSON object with keys: persona, engagementInsights, recommendedDiscussions, dosAndDonts, nextBestAction. Use persona fields: type, summary, communicationStyle, decisionDrivers, motivations, concerns. engagementInsights is an array of objects with 'text'. recommendedDiscussions is an array of objects: topic, rationale, approach, timing. dosAndDonts contains 'dos' and 'donts' arrays. nextBestAction is a short actionable sentence. Only output valid JSON.`;
    const userPrompt = `Doctor info: ${JSON.stringify(doctor)}\n\nVisits (latest first): ${JSON.stringify(visits.slice(0,5))}\n\nProducts (summary): ${JSON.stringify((products || []).slice(0,10))}\n\nRespond only with the JSON object described.`;

    const resp = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${OPENAI_API_KEY}`
      },
      body: JSON.stringify({
        model: OPENAI_MODEL,
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userPrompt }
        ],
        temperature: 0.2,
        max_tokens: 800
      })
    });
    if (!resp.ok) {
      throw new Error('OpenAI API error: ' + (await resp.text()));
    }
    const data = await resp.json();
    const content = data.choices && data.choices[0] && data.choices[0].message && data.choices[0].message.content;
    let parsed;
    try {
      parsed = JSON.parse(content);
    } catch (err) {
      // Try to extract JSON substring
      const match = content && content.match(/\{[\s\S]*\}/);
      if (match) {
        parsed = JSON.parse(match[0]);
      } else {
        throw new Error('OpenAI did not return JSON: ' + content);
      }
    }
    return parsed;
  },

  _localAIGenerate(doctor: Doctor, visits: Visit[]): AIGeneratedInsights {
    // ...existing local logic from previous implementation...
    const outcomes = visits.map(v => v.outcome);
    const totalSamples = visits.reduce((sum, v) => sum + v.samplesGiven, 0);
    const avgDuration = visits.length ? Math.round(visits.reduce((sum, v) => sum + v.duration, 0) / visits.length) : 0;
    const veryPositiveCount = outcomes.filter(o => o === 'Very Positive').length;
    return {
      persona: {
        type: veryPositiveCount > 5 ? 'Innovation Seeker' : 'Evidence Driven',
        summary: `${doctor.personalInfo.name} is a ${doctor.professionalInfo.specialty} who values clinical data and real-world outcomes.`,
        communicationStyle: 'Direct and data-focused.',
        decisionDrivers: ['Clinical evidence', 'Patient outcomes', 'Safety profiles'],
        motivations: ['Better patient outcomes', 'Academic collaboration'],
        concerns: ['Long-term safety', 'Cost-effectiveness']
      },
      engagementInsights: [
        { text: `Prefers ${doctor.preferences.preferredTime} on ${doctor.preferences.preferredDays.join('/')} — avg ${avgDuration}m discussions` },
        { text: `High sample usage: ${totalSamples} over ${visits.length} visits` }
      ],
      recommendedDiscussions: [
        { topic: 'Clinical data deep-dive', rationale: 'Values evidence', approach: 'Share trials and outcomes', timing: doctor.preferences.preferredTime }
      ],
      dosAndDonts: {
        dos: ['Bring trial data', 'Share patient outcomes'],
        donts: ['Avoid generic pitches']
      },
      nextBestAction: 'Share key trial publications and schedule a follow-up.'
    };
  }
};

